import React, {
  useEffect,
  memo,
  useMemo,
}                                      from 'react';
import { useDispatch }                 from 'react-redux';
import { RouteComponentProps }         from 'react-router-dom';
import { withWidth, WithWidth }        from '@material-ui/core';
import { ICreditUnion }                from '@typings/accountManagement';
import {
  EAccountPage,
  EAccountSubpage,
}                                      from '@enums/accountManagement';
import { useFetchAccountById }         from '@modules/accountManagement/queries/accountQueries';
import { setCurrentCUID }              from '@modules/accountManagement/accountManagementActions';
import { Loader }                      from '@components/Loader/Loader';
import { AccountPartnersContainer }    from '@modules/accountManagement/containers/AccountPartnersContainer';
import { ReportsContainer }            from '@modules/accountManagement/containers/ReportsContainer';
import { SalespersonContainer }        from '@modules/accountManagement/containers/SalespersonContainer';
import { UsersContainer }              from '@modules/accountManagement/containers/UsersContainer';
import { ReportsDetailsContainer }     from '@modules/accountManagement/containers/ReportsDetailsContainer';
import { AccountStatisticsPage }       from '@modules/accountManagement/pages/AccountStatisticsPage';
import { AccountSearchContainer }      from './AccountSearchContainer';
import { AccountInfoContainer }        from './AccountInfoContainer';
import { AccountCommentsContainer }    from './AccountCommentsContainer';
import { AccountInvoicesContainer }    from './AccountInvoicesContainer';
import { AccountAuctionsContainer }    from './AccountAuctionsContainer';
import { AuctionDetailsContainer }     from './AuctionDetailsContainer';
import { AuctionPhotosContainer }      from './AuctionPhotosContainer';

interface IParams {
  id      : string;
  page    : EAccountPage;
  subid   : string;
  subpage : EAccountSubpage;
}

const AccountManagementMain: React.FC<RouteComponentProps<IParams> & WithWidth> = ({
  width,
  match: {
    params: {
      id,
      page,
      subid,
      subpage,
    },
  },
  history: {
    push,
    replace,
  },
}) => {
  const dispatch = useDispatch();

  const isValidCuid = useMemo<boolean>(
    () => !!id && id.length >= 5,
    [id],
  );

  // #region API INTEGRATION
  const {
    data: cuInfo,
    isLoading,
  } = useFetchAccountById(id, { enabled: isValidCuid });
  // #endregion

  useEffect( // need to NavBar navigation
    () => {
      if (isValidCuid) {
        dispatch(setCurrentCUID(id));
      }

      return () => {
        dispatch(setCurrentCUID(null));
      };
    },
    [dispatch, id, isValidCuid],
  );

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      {page === EAccountPage.Search && (
        <AccountSearchContainer />
      )}

      {[EAccountPage.Add, EAccountPage.Edit].includes(page) && (
        <AccountInfoContainer
          push           = {push}
          width          = {width}
          isNewAccount   = {page === EAccountPage.Add}
          currentAccount = {cuInfo?.cuData ?? null}
          currentLogo    = {cuInfo?.cuLogo ?? null}
          cuid           = {id}
        />
      )}

      {page === EAccountPage.Comments && (
        <AccountCommentsContainer
          push           = {push}
          currentAccount = {cuInfo?.cuData as ICreditUnion}
          cuid           = {id}
        />
      )}

      {page === EAccountPage.Invoices && (
        <AccountInvoicesContainer
          push           = {push}
          replace        = {replace}
          invoiceId      = {subid}
          currentAccount = {cuInfo?.cuData as ICreditUnion}
          cuid           = {id}
        />
      )}

      {page === EAccountPage.Auctions && !subpage && (
        <AccountAuctionsContainer
          push        = {push}
          accountData = {cuInfo?.cuData as ICreditUnion}
          cuid        = {id}
        />
      )}

      {page === EAccountPage.Auctions && subpage === EAccountSubpage.Details && (
        <AuctionDetailsContainer
          push           = {push}
          width          = {width}
          auctionId      = {subid}
          currentAccount = {cuInfo?.cuData as ICreditUnion}
        />
      )}

      {page === EAccountPage.Auctions && subpage === EAccountSubpage.Photos && (
        <AuctionPhotosContainer auctionId={subid} />
      )}

      {page === EAccountPage.Partners && (
        <AccountPartnersContainer
          push        = {push}
          accountData = {cuInfo?.cuData as ICreditUnion}
          cuid        = {id}
        />
      )}

      {page === EAccountPage.Salespersons && (
        <SalespersonContainer push={push} />
      )}

      {page === EAccountPage.Users && (
        <UsersContainer push={push} />
      )}

      {page === EAccountPage.Reports && !subpage && (
        <ReportsContainer push={push} />
      )}

      {page === EAccountPage.Reports && subpage === EAccountSubpage.ReportDetails && (
        <ReportsDetailsContainer
          year  = {+id}
          month = {+subid}
          push  = {push}
        />
      )}

      {page === EAccountPage.Statistics && (
        <AccountStatisticsPage
          push   = {push}
          cuInfo = {cuInfo?.cuData as ICreditUnion}
        />
      )}
    </>
  );
};

export const AccountManagementMainContainer = memo(withWidth()(AccountManagementMain));
